import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, LogOut, FileImage, FileVideo, FileText, Palette, Type, Clock, ShieldCheck, Eye, LayoutDashboard, ExternalLink, Pencil, X, Trash2 } from "lucide-react";
import { api, API, getUser, logout as apiLogout } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Logo } from "@/components/SiteChrome";

const FILE_TYPES = [
  { v: "image", label: "Image / carousel (one or many)", category: "content_library", needsFile: true, multi: true },
  { v: "banner", label: "Banner", category: "content_library", needsFile: true, multi: true },
  { v: "template", label: "Template", category: "content_library", needsFile: true, multi: true },
  { v: "video", label: "Video (Reel / YouTube)", category: "content_library", needsFile: false, supportsExternal: true },
  { v: "caption", label: "Caption template", category: "content_library", needsFile: false, isCaption: true },
  { v: "brand_logo", label: "Brand logo (PNG)", category: "brand_kit", needsFile: true, multi: true },
  { v: "brand_color", label: "Brand color (hex)", category: "brand_kit", needsFile: false, isColor: true },
  { v: "brand_font", label: "Brand font", category: "brand_kit", needsFile: false, isFont: true },
];

const BLANK = { name: "", description: "", file_type: "image", category: "content_library", caption_text: "", external_url: "", hex: "", font_family: "" };
const fileUrl = (path) => (path ? `${API}/files/${path}` : null);
const typeIcon = (t) => (t === "video" ? FileVideo : t === "caption" ? FileText : t === "brand_color" ? Palette : t === "brand_font" ? Type : FileImage);

export default function ContentManager() {
  const nav = useNavigate();
  const user = getUser();
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [customPh, setCustomPh] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [files, setFiles] = useState([]); // up to 10 images (carousel) or a single video
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [proposeTarget, setProposeTarget] = useState(null);
  const [proposeForm, setProposeForm] = useState({ name: "", description: "" });
  const [proposing, setProposing] = useState(false);

  const typeDef = useMemo(() => FILE_TYPES.find((t) => t.v === form.file_type) || FILE_TYPES[0], [form.file_type]);
  const maxMb = me?.max_upload_mb || 25;

  const loadResources = () => api.get("/cm/resources").then((r) => setItems(r.data.items || []));
  const loadProposals = () => api.get("/cm/edit-proposals").then((r) => setProposals(r.data.items || [])).catch(() => {});

  const pendingByRes = useMemo(() => {
    const m = {};
    (proposals || []).forEach((p) => {
      const cur = m[p.resource_id];
      if (!cur || new Date(p.created_at) > new Date(cur.created_at)) m[p.resource_id] = p;
    });
    return m;
  }, [proposals]);

  useEffect(() => {
    if (!user || (user.role !== "admin" && !user.content_manager)) { nav("/login"); return; }
    api.get("/cm/me")
      .then((r) => setMe(r.data))
      .catch(() => { toast.error("Content Manager access not available"); nav("/login"); });
    loadResources();
    loadProposals();
    api.get("/cm/placeholders").then((r) => setCustomPh(r.data.items || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPropose = (r) => {
    setProposeForm({ name: r.name || "", description: r.description || "" });
    setProposeTarget(r);
  };

  const submitPropose = async () => {
    if (!proposeForm.name.trim()) return toast.error("Name is required");
    setProposing(true);
    try {
      await api.post(`/cm/resources/${proposeTarget.id}/propose-edit`, {
        name: proposeForm.name.trim(),
        description: proposeForm.description,
      });
      toast.success("Edit submitted — an admin will review it before it goes live");
      setProposeTarget(null);
      loadProposals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit edit");
    } finally {
      setProposing(false);
    }
  };

  const signOut = async () => { await apiLogout(); nav("/login"); };

  const onTypeChange = (v) => {
    const def = FILE_TYPES.find((t) => t.v === v);
    setForm({ ...form, file_type: v, category: def?.category || "content_library" });
    setFiles([]);
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setFiles((prev) => {
      const allowMulti = !!typeDef.multi;
      const merged = allowMulti ? [...prev, ...incoming] : incoming.slice(0, 1);
      if (merged.length > 10) toast.error("Up to 10 images per carousel.");
      return merged.slice(0, 10);
    });
  };

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const [deletingId, setDeletingId] = useState(null);
  const deletePending = async (r) => {
    if (!window.confirm(`Delete your pending upload "${r.name}"? This can't be undone.`)) return;
    setDeletingId(r.id);
    try {
      await api.delete(`/cm/resources/${r.id}`);
      toast.success("Upload deleted");
      setPreview((p) => (p?.id === r.id ? null : p));
      loadResources();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  const chunkedUpload = async (theFile, fields, onProgress) => {
    const CHUNK = 4 * 1024 * 1024;
    const rawId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const uploadId = rawId.replace(/[^A-Za-z0-9_-]/g, "");
    const total = Math.ceil(theFile.size / CHUNK);
    for (let i = 0; i < total; i++) {
      const blob = theFile.slice(i * CHUNK, Math.min(theFile.size, (i + 1) * CHUNK));
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("chunk_index", String(i));
      fd.append("total_chunks", String(total));
      fd.append("chunk", blob, theFile.name);
      await api.post("/cm/resources/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onProgress(Math.round(((i + 1) / total) * 95));
    }
    await api.post("/cm/resources/chunk/complete", { upload_id: uploadId, total_chunks: total, filename: theFile.name, content_type: theFile.type || "", ...fields });
    onProgress(100);
  };

  const upload = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    if (typeDef.needsFile && files.length === 0) return toast.error("File required");
    if (typeDef.isColor && !/^#?[0-9a-f]{6}$/i.test((form.hex || "").trim())) return toast.error("Enter a 6-digit hex like #A855F7");
    if (typeDef.isFont && !form.font_family.trim()) return toast.error("Font family required");
    if (form.file_type === "video" && files.length === 0 && !form.external_url.trim()) return toast.error("Provide a file OR an external URL");
    const maxBytes = maxMb * 1024 * 1024;
    const oversize = files.find((f) => f.size > maxBytes);
    if (oversize) return toast.error(`"${oversize.name}" is ${(oversize.size / 1048576).toFixed(1)} MB — over the ${maxMb} MB limit.`);

    const fields = { name: form.name.trim(), file_type: form.file_type, category: form.category };
    if (form.description.trim()) fields.description = form.description.trim();
    if (typeDef.isCaption) fields.caption_text = form.caption_text;
    if (typeDef.supportsExternal && form.external_url.trim()) fields.external_url = form.external_url.trim();
    if (typeDef.isColor) fields.meta_json = JSON.stringify({ hex: form.hex.trim().replace(/^#?/, "#") });
    if (typeDef.isFont) fields.meta_json = JSON.stringify({ font_family: form.font_family.trim() });

    const CHUNK = 4 * 1024 * 1024;
    setUploading(true); setProgress(0);
    try {
      // A single large file (e.g. a video) uses the chunked path. Everything else —
      // including a multi-image carousel — goes as one multipart request with files[].
      if (files.length === 1 && files[0].size > CHUNK) {
        await chunkedUpload(files[0], fields, setProgress);
      } else {
        const fd = new FormData();
        Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
        files.forEach((f) => fd.append("files", f));
        await api.post("/cm/resources", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Submitted — awaiting admin approval");
      setForm(BLANK); setFiles([]); loadResources();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false); setProgress(0);
    }
  };

  const filtered = filter === "all"
    ? items
    : filter === "mine"
      ? items.filter((r) => r.uploaded_by && me?.email && r.uploaded_by === me.email)
      : items.filter((r) => r.category === filter);
  const myPending = items.filter((r) => r.pending_approval && r.uploaded_by === me?.email);
  const myCount = items.filter((r) => r.uploaded_by && me?.email && r.uploaded_by === me.email).length;

  return (
    <div className="min-h-screen bg-transparent text-white" data-testid="content-manager-page">
      <header className="border-b border-zinc-900">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="hidden rounded-full bg-[#A855F7]/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-[#A855F7] sm:inline">Content Manager</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-zinc-400 sm:inline">{me?.name || user?.name}</span>
            <button onClick={() => nav("/dashboard")} data-testid="cm-goto-dashboard" className="inline-flex items-center gap-1.5 rounded-md border border-[#A855F7]/40 bg-[#A855F7]/10 px-3 py-1.5 text-sm font-semibold text-[#A855F7] hover:bg-[#A855F7]/20"><LayoutDashboard className="h-4 w-4" /> <span className="hidden sm:inline">Ambassador dashboard</span><span className="sm:hidden">Dashboard</span></button>
            <button onClick={signOut} data-testid="cm-signout" className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 px-3 py-1.5 text-sm text-zinc-300 hover:text-white"><LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sign out</span></button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-8">
        <h1 className="font-display text-3xl font-black tracking-tighter">Content workspace</h1>
        <p className="mt-1 text-sm text-zinc-400">Upload resources to the library — each upload is reviewed by an admin before it goes live. Want to earn points and participate too? Head to your <button onClick={() => nav("/dashboard")} className="font-semibold text-[#A855F7] hover:underline">Ambassador dashboard</button>.</p>

        <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
          {/* Upload panel */}
          <div className="min-w-0 space-y-6">
            <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
              <h2 className="font-display text-lg font-bold">Upload resource</h2>
              <div className="mt-4 space-y-3">
                <select data-testid="cm-type" value={form.file_type} onChange={(e) => onTypeChange(e.target.value)} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm">
                  <optgroup label="Content library">
                    {FILE_TYPES.filter((t) => t.category === "content_library").map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                  </optgroup>
                  <optgroup label="Brand kit">
                    {FILE_TYPES.filter((t) => t.category === "brand_kit").map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                  </optgroup>
                </select>

                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <span>Goes into:</span>
                  <span className="rounded bg-zinc-800 px-2 py-1 font-bold uppercase tracking-wider text-zinc-200">{form.category === "brand_kit" ? "Brand Kit" : "Content Library"}</span>
                </div>

                <input data-testid="cm-name" placeholder="Resource name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />

                <textarea data-testid="cm-desc" rows={2} placeholder="Description (optional) — supports [REF_LINK], [CODE], [NAME] & custom placeholders" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />

                {typeDef.isCaption && (
                  <textarea data-testid="cm-caption" rows={3} placeholder="Caption text" value={form.caption_text} onChange={(e) => setForm({ ...form, caption_text: e.target.value })} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
                )}
                {typeDef.supportsExternal && (
                  <input data-testid="cm-external" placeholder="External URL (Instagram / YouTube link) — optional if file uploaded" value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
                )}
                {typeDef.isColor && (
                  <input data-testid="cm-hex" placeholder="#A855F7" value={form.hex} onChange={(e) => setForm({ ...form, hex: e.target.value })} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
                )}
                {typeDef.isFont && (
                  <input data-testid="cm-font" placeholder="Font family (e.g. Inter)" value={form.font_family} onChange={(e) => setForm({ ...form, font_family: e.target.value })} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
                )}

                {(typeDef.needsFile || form.file_type === "video") && (
                  <div>
                    <input
                      data-testid="cm-file"
                      type="file"
                      multiple={!!typeDef.multi}
                      accept={typeDef.multi ? "image/*" : undefined}
                      onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                      disabled={files.length >= 10}
                      className="block w-full text-sm text-zinc-300 file:mr-3 file:rounded file:border-0 file:bg-[#A855F7] file:px-3 file:py-1 file:text-xs file:font-bold file:text-black disabled:opacity-50"
                    />
                    <p className="mt-1 text-[11px] text-zinc-500">
                      Max {maxMb} MB each.{typeDef.multi ? ` Add up to 10 images for a carousel (${files.length}/10).` : " Large files upload in chunks."}
                    </p>
                    {files.length > 0 && (
                      <div data-testid="cm-file-previews" className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5">
                        {files.map((f, i) => {
                          const isImg = f.type?.startsWith("image/");
                          const url = isImg ? URL.createObjectURL(f) : null;
                          return (
                            <div key={i} className="group relative aspect-square overflow-hidden rounded-md border border-zinc-800 bg-zinc-900">
                              {isImg ? (
                                <img src={url} alt={`slide ${i + 1}`} className="h-full w-full object-cover" />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] text-zinc-500">{f.name}</div>
                              )}
                              <span className="absolute left-1 top-1 rounded bg-black/70 px-1 text-[10px] font-bold text-white">{i + 1}</span>
                              <button
                                type="button"
                                data-testid={`cm-remove-file-${i}`}
                                onClick={() => removeFile(i)}
                                className="absolute right-1 top-1 rounded-full bg-black/70 p-0.5 text-white opacity-0 transition group-hover:opacity-100"
                                aria-label="Remove image"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                <button onClick={upload} disabled={uploading} data-testid="cm-upload-btn" className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-[#A855F7] px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">
                  <Upload className="h-4 w-4" /> {uploading ? (progress > 0 ? `Uploading… ${progress}%` : "Uploading…") : "Submit for approval"}
                </button>
                {uploading && progress > 0 && (
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-800"><div className="h-full rounded-full bg-[#A855F7] transition-all" style={{ width: `${progress}%` }} /></div>
                )}
              </div>
            </div>

            {/* Placeholders reference (read-only) */}
            <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
              <h2 className="font-display text-lg font-bold">Caption placeholders</h2>
              <p className="mt-1 text-xs text-zinc-500">Use these inside any description/caption — they auto-replace for each ambassador.</p>
              <p className="mt-3 text-[11px] font-bold uppercase tracking-wider text-zinc-500">Built-in</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {["REF_LINK", "CODE", "NAME"].map((k) => <span key={k} className="rounded bg-zinc-800 px-2 py-1 font-mono text-[11px] text-zinc-300">[{k}]</span>)}
              </div>
              {customPh.length > 0 && (
                <>
                  <p className="mt-3 text-[11px] font-bold uppercase tracking-wider text-zinc-500">Custom</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {customPh.map((p) => <span key={p.key} className="rounded bg-zinc-800 px-2 py-1 font-mono text-[11px] text-zinc-300">[{p.key}]</span>)}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Read-only library */}
          <div className="min-w-0">
            {myPending.length > 0 && (
              <button onClick={() => setFilter("mine")} className="mb-4 flex w-full items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-left text-sm text-amber-200 hover:bg-amber-500/15" data-testid="cm-pending-banner">
                <Clock className="h-4 w-4" /> You have {myPending.length} upload{myPending.length === 1 ? "" : "s"} awaiting admin approval — click to see your uploads.
              </button>
            )}
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <h2 className="font-display text-xl font-bold">All resources ({filtered.length})</h2>
              <div className="flex flex-wrap gap-1.5">
                {["all", "brand_kit", "content_library", "community_originals"].map((f) => (
                  <button key={f} onClick={() => setFilter(f)} data-testid={`cm-filter-${f}`} className={`rounded-full px-3 py-1 text-xs font-semibold ${filter === f ? "bg-[#A855F7] text-white" : "bg-zinc-900 text-zinc-400 hover:text-white"}`}>{f.replace(/_/g, " ")}</button>
                ))}
                <button onClick={() => setFilter("mine")} data-testid="cm-filter-mine" className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${filter === "mine" ? "bg-emerald-500 text-black" : "bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"}`}>
                  my uploads{myCount > 0 ? ` (${myCount})` : ""}
                </button>
              </div>
            </div>

            <div className="space-y-2" data-testid="cm-resource-list">
              {filtered.length === 0 && <p className="rounded-xl border border-zinc-900 bg-zinc-950 p-8 text-center text-zinc-500">{filter === "mine" ? "You haven't uploaded anything yet — use the form on the left to submit your first resource." : "No resources."}</p>}
              {filtered.map((r) => {
                const Icon = typeIcon(r.file_type);
                const isImg = ["image", "banner", "template", "brand_logo", "logo"].includes(r.file_type) && r.file_path;
                const isPending = r.pending_approval;
                const prop = pendingByRes[r.id];
                const canDelete = r.pending_approval && r.uploaded_by && me?.email && r.uploaded_by === me.email;
                return (
                  <div key={r.id} onClick={() => setPreview(r)} data-testid={`cm-res-card-${r.id}`} className="flex cursor-pointer items-start gap-3 rounded-xl border border-zinc-900 bg-zinc-950 p-3 transition-colors hover:border-zinc-700 hover:bg-zinc-900/60">
                    {isImg ? (
                      <img src={fileUrl(r.file_path)} alt="" className="h-12 w-12 shrink-0 rounded border border-zinc-800 bg-white object-contain p-1" />
                    ) : r.meta?.hex ? (
                      <div className="h-12 w-12 shrink-0 rounded border border-zinc-800" style={{ background: r.meta.hex }} />
                    ) : (
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded border border-zinc-800 bg-zinc-900"><Icon className="h-5 w-5 text-zinc-500" /></div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate font-semibold">{r.name}</p>
                        {r.image_paths?.length > 1 && <span className="inline-flex items-center gap-1 rounded bg-[#A855F7]/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#A855F7]" data-testid={`cm-carousel-badge-${r.id}`}><FileImage className="h-3 w-3" /> {r.image_paths.length} slides</span>}
                        {isPending && <span className="inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300"><Clock className="h-3 w-3" /> Pending</span>}
                        {!isPending && r.is_visible && <span className="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-300"><Eye className="h-3 w-3" /> Live</span>}
                        {!isPending && !r.is_visible && <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-zinc-400">Draft</span>}
                        {prop?.status === "pending" && <span className="inline-flex items-center gap-1 rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-300" data-testid={`cm-edit-pending-${r.id}`}><Pencil className="h-3 w-3" /> edit pending</span>}
                        {prop?.status === "rejected" && <span className="rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-300">edit rejected</span>}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-zinc-500">
                        <span className="rounded bg-zinc-800 px-1.5 py-0.5">{(r.category || "").replace(/_/g, " ")}</span>
                        <span>{r.file_type}</span>
                        {r.uploaded_by && <span>· by {r.uploaded_by}</span>}
                      </div>
                      {r.description && <p className="mt-1 line-clamp-2 break-words text-xs text-zinc-400">{r.description}</p>}
                      <p className="mt-1 text-[11px] font-semibold text-[#A855F7]">Click to view full details →</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); openPropose(r); }}
                      data-testid={`cm-propose-edit-${r.id}`}
                      title="Propose an edit to the title / description (admin approves before it goes live)"
                      className="shrink-0 rounded-md border border-zinc-800 p-1.5 text-zinc-400 hover:border-[#A855F7]/40 hover:bg-[#A855F7]/10 hover:text-[#A855F7]"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    {canDelete && (
                      <button
                        onClick={(e) => { e.stopPropagation(); deletePending(r); }}
                        disabled={deletingId === r.id}
                        data-testid={`cm-delete-pending-${r.id}`}
                        title="Delete this pending upload (only yours, before an admin reviews it)"
                        className="shrink-0 rounded-md border border-zinc-800 p-1.5 text-zinc-400 hover:border-rose-500/40 hover:bg-rose-500/10 hover:text-rose-400 disabled:opacity-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="mt-4 flex items-center gap-1.5 text-xs text-zinc-600"><ShieldCheck className="h-3.5 w-3.5" /> You can upload new items and propose title/description edits — every change is reviewed by an admin before it goes live.</p>
          </div>
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-zinc-800 bg-zinc-950 text-white" data-testid="cm-preview-modal">
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle className="pr-6 font-display text-xl">{preview.name}</DialogTitle>
                <DialogDescription className="sr-only">Resource preview with full poster and description</DialogDescription>
              </DialogHeader>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                <span className="rounded bg-zinc-800 px-1.5 py-0.5">{(preview.category || "").replace(/_/g, " ")}</span>
                <span>{preview.file_type}</span>
                {preview.pending_approval && <span className="rounded bg-amber-500/15 px-1.5 py-0.5 font-bold text-amber-300">Pending approval</span>}
                {!preview.pending_approval && preview.is_visible && <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 font-bold text-emerald-300">Live</span>}
                {preview.uploaded_by && <span>· by {preview.uploaded_by}</span>}
              </div>

              {preview.pending_approval && preview.uploaded_by === me?.email && (
                <button
                  onClick={() => deletePending(preview)}
                  disabled={deletingId === preview.id}
                  data-testid="cm-preview-delete"
                  className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-1.5 text-sm font-semibold text-rose-300 hover:bg-rose-500/20 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" /> Delete this pending upload
                </button>
              )}

              {/* Poster / media */}
              {["image", "banner", "template", "brand_logo", "logo"].includes(preview.file_type) && (preview.image_paths?.length > 1) ? (
                <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="cm-preview-carousel">
                  {preview.image_paths.map((p, i) => (
                    <div key={i} className="relative overflow-hidden rounded-lg border border-zinc-800 bg-white">
                      <img src={fileUrl(p)} alt={`${preview.name} slide ${i + 1}`} className="h-40 w-full object-contain" />
                      <span className="absolute left-1 top-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-bold text-white">{i + 1}/{preview.image_paths.length}</span>
                    </div>
                  ))}
                </div>
              ) : ["image", "banner", "template", "brand_logo", "logo"].includes(preview.file_type) && preview.file_path && (
                <img src={fileUrl(preview.file_path)} alt={preview.name} className="mt-4 max-h-[55vh] w-full rounded-lg border border-zinc-800 bg-white object-contain" data-testid="cm-preview-image" />
              )}
              {preview.file_type === "video" && preview.file_path && (
                <video src={fileUrl(preview.file_path)} controls className="mt-4 max-h-[55vh] w-full rounded-lg border border-zinc-800 bg-black" />
              )}
              {preview.meta?.hex && (
                <div className="mt-4 flex items-center gap-3">
                  <div className="h-16 w-16 rounded-lg border border-zinc-800" style={{ background: preview.meta.hex }} />
                  <span className="font-mono text-sm">{preview.meta.hex}</span>
                </div>
              )}
              {preview.external_url && (
                <a href={preview.external_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#A855F7] hover:underline" data-testid="cm-preview-link"><ExternalLink className="h-4 w-4" /> Open original link</a>
              )}

              {preview.description && (
                <div className="mt-5">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Description / caption</p>
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm text-zinc-200" data-testid="cm-preview-desc">{preview.description}</p>
                </div>
              )}
              {preview.caption_text && (
                <div className="mt-4">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Caption text</p>
                  <p className="mt-1 whitespace-pre-wrap break-words rounded-lg border border-zinc-800 bg-zinc-900 p-3 text-sm text-zinc-200">{preview.caption_text}</p>
                </div>
              )}
              {!preview.description && !preview.caption_text && !preview.meta?.hex && (
                <p className="mt-5 text-sm text-zinc-500">No description provided for this resource.</p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!proposeTarget} onOpenChange={(o) => !o && setProposeTarget(null)}>
        <DialogContent className="max-w-lg border-zinc-800 bg-zinc-950 text-white" data-testid="cm-propose-modal">
          {proposeTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">Propose an edit</DialogTitle>
                <DialogDescription className="text-zinc-400">
                  Your change won't go live immediately — an admin reviews the title &amp; description before publishing. The file/image can't be changed here.
                </DialogDescription>
              </DialogHeader>
              <div className="mt-2 space-y-3">
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-2 text-xs text-zinc-500">
                  <span className="rounded bg-zinc-800 px-1.5 py-0.5">{(proposeTarget.category || "").replace(/_/g, " ")}</span>
                  <span className="ml-2">{proposeTarget.file_type}</span>
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Title / name</label>
                  <input
                    data-testid="cm-propose-name"
                    value={proposeForm.name}
                    onChange={(e) => setProposeForm({ ...proposeForm, name: e.target.value })}
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm"
                    placeholder="Resource title"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Description</label>
                  <textarea
                    data-testid="cm-propose-desc"
                    rows={4}
                    value={proposeForm.description}
                    onChange={(e) => setProposeForm({ ...proposeForm, description: e.target.value })}
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm"
                    placeholder="Description (optional) — supports [REF_LINK], [CODE], [NAME]"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setProposeTarget(null)} className="rounded-md border border-zinc-800 px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-900" data-testid="cm-propose-cancel">Cancel</button>
                  <button onClick={submitPropose} disabled={proposing} className="rounded-md bg-[#A855F7] px-4 py-2 text-sm font-bold text-white hover:bg-[#9333EA] disabled:opacity-60" data-testid="cm-propose-submit">
                    {proposing ? "Submitting…" : "Submit for approval"}
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
